import { useEffect, RefObject } from 'react';
import { setFocusMode } from 't-lib-ui/structure';
import { findNearestSidebarItem } from './LeftSidebarFocus';

function isVisible(el: HTMLElement): boolean {
  if (!el.offsetParent) return false;
  const style = getComputedStyle(el);
  return style.display != 'none' && style.visibility != 'hidden' && style.opacity != '0';
}

function hasVisibleModal(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>('.flowFormModal'))
    .some(el => el.offsetParent || el.matches(':modal'));
}

function isFullyVisible(el: HTMLElement, containerRect: DOMRect): boolean {
  const r = el.getBoundingClientRect();
  return r.top >= containerRect.top && r.bottom <= containerRect.bottom && r.height > 0;
}

function isPartiallyVisible(el: HTMLElement, containerRect: DOMRect): boolean {
  const r = el.getBoundingClientRect();
  const verticallyVisible = r.bottom > containerRect.top && r.top < containerRect.bottom;
  return verticallyVisible && r.height > 0;
}

function getFocusableItems(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>('.flowFocussable'))
    .filter(el => isVisible(el));
}

interface ScoredCandidate {
  el: HTMLElement;
  distance: number;
  verticalGap: number;
  horizontalGap: number;
  centerDistance: number;
}

function evaluateCandidates(sourceRect: DOMRect, candidates: HTMLElement[]): ScoredCandidate[] {
  const sourceCenterY = (sourceRect.top + sourceRect.bottom) / 2;

  const scored = candidates
    .map(el => {
      const r = el.getBoundingClientRect();

      // ArrowRight should not choose elements fully left of the source element.
      if (r.right <= sourceRect.left) return null;

      const horizontalGap = Math.max(0, r.left - sourceRect.right);
      const verticalGap = Math.max(0, r.top - sourceRect.bottom, sourceRect.top - r.bottom);
      const centerDistance = Math.abs(((r.top + r.bottom) / 2) - sourceCenterY);

      return {
        el,
        distance: Math.hypot(horizontalGap, verticalGap),
        verticalGap,
        horizontalGap,
        centerDistance,
      };
    })
    .filter((it): it is ScoredCandidate => !!it);

  scored.sort((a, b) =>
    a.centerDistance - b.centerDistance ||
    a.distance - b.distance ||
    a.verticalGap - b.verticalGap ||
    a.horizontalGap - b.horizontalGap
  );

  return scored;
}

function scoreCandidates(sourceRect: DOMRect, candidates: HTMLElement[]): HTMLElement | null {
  return evaluateCandidates(sourceRect, candidates)[0]?.el ?? null;
}

/** Find nearest focusable element in PostView at a similar vertical position.
 *  Prefers elements directly to the right, but always picks the closest by geometry. */
export function findNearestInPostView(sourceEl: HTMLElement): HTMLElement | null {
  const postView = document.querySelector('.postView') as HTMLElement | null;
  if (!postView) return null;

  const postViewRect = postView.getBoundingClientRect();
  const candidates = Array.from(postView.querySelectorAll<HTMLElement>('.flowFocussable'))
    .filter(el => isVisible(el) && isPartiallyVisible(el, postViewRect));

  if (!candidates.length) return null;

  const sourceRect = sourceEl.getBoundingClientRect();
  const nonSideway = candidates.filter(el => !el.classList.contains('sidewayFocus'));

  // Entering PostView from HeadlineList should land on primary content first.
  // Only fall back to sideway controls if nothing else is available.
  return scoreCandidates(sourceRect, nonSideway) ?? scoreCandidates(sourceRect, candidates);
}

export function useHeadlineListFocus(headlineListRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const headlineList = headlineListRef.current;
    if (!headlineList) return;

    function mouseHandler() {
      setFocusMode('hidden');
    }

    function handleKeyDown(ev: KeyboardEvent) {
      if (ev.defaultPrevented) return;
      if (hasVisibleModal()) return;

      const focused = document.activeElement as HTMLElement;

      // Only handle if focus is within this headlineList
      if (!headlineList!.contains(focused)) return;

      const key = ev.key;

      // Non-navigation keys pass through
      if (key != 'ArrowDown' && key != 'ArrowUp' && key != 'ArrowLeft' && key != 'ArrowRight' && key != 'Enter') return;

      setFocusMode('visible');

      const items = getFocusableItems(headlineList!);
      const idx = items.indexOf(focused);

      if (key == 'ArrowDown' || key == 'ArrowUp') {
        const dir = key == 'ArrowDown' ? 1 : -1;
        const next = idx >= 0 ? items[idx + dir] : (dir == 1 ? items[0] : null);

        // At first item, can't go up
        if (!next && dir == -1) {
          ev.preventDefault();
          ev.stopPropagation();
          return;
        }

        if (next) {
          ev.preventDefault();
          ev.stopPropagation();
          next.focus();
        }
        return;
      }

      if (key == 'Enter') {
        // Trigger click on the focused headline item
        if (focused.classList.contains('headlineItemCard') || focused.classList.contains('markAllReadBottomButton') || focused.tagName == 'BUTTON') {
          ev.preventDefault();
          ev.stopPropagation();
          focused.click();
        }
        return;
      }

      if (key == 'ArrowRight') {
        ev.preventDefault();
        ev.stopPropagation();
        const target = findNearestInPostView(focused);
        if (target) target.focus({ preventScroll: true });
        return;
      }

      if (key == 'ArrowLeft') {
        ev.preventDefault();
        ev.stopPropagation();
        const target = findNearestSidebarItem(focused);
        if (target) target.focus({ preventScroll: true });
        return;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    headlineList.addEventListener('mousedown', mouseHandler);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      headlineList.removeEventListener('mousedown', mouseHandler);
    };
  }, [headlineListRef]);
}

/** Find nearest fully-visible headline item at a similar vertical position to the source element */
export function findNearestHeadlineItem(sourceEl: HTMLElement): HTMLElement | null {
  const headlineList = document.querySelector('.headlineList') as HTMLElement | null;
  if (!headlineList) return null;

  const itemsContainer = headlineList.querySelector('.headlineItems') as HTMLElement | null;
  if (!itemsContainer) return null;

  const containerRect = itemsContainer.getBoundingClientRect();
  const candidates = Array.from(itemsContainer.querySelectorAll<HTMLElement>('.headlineItemCard.flowFocussable'))
    .filter(el => isVisible(el) && isFullyVisible(el, containerRect));

  if (!candidates.length) return null;

  const sourceRect = sourceEl.getBoundingClientRect();
  const sourceCenter = (sourceRect.top + sourceRect.bottom) / 2;

  // Find nearest by vertical center distance
  let best: HTMLElement | null = null;
  let bestDist = Infinity;
  for (const el of candidates) {
    const r = el.getBoundingClientRect();
    const center = (r.top + r.bottom) / 2;
    const dist = Math.abs(center - sourceCenter);
    if (dist < bestDist) {
      bestDist = dist;
      best = el;
    }
  }
  return best;
}
